import { NetworkStatus, gql, useQuery } from '@apollo/client';
import {
  Box,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  Popover,
  PopoverContent,
  PopoverProps,
  PopoverTrigger,
  Portal,
  Spinner,
  Tag,
  TagLabel,
  Text,
} from '@chakra-ui/react';
import * as React from 'react';
import FocusLock from 'react-focus-lock';

import { PopoverMenuItem, PopoverMenuList } from '@/components/PopoverMenu';
import { SelectButton } from '@/components/SelectButton';
import { XIcon } from '@/imports/ui/chakra/feather';
import { useDebouncedValue } from '@/imports/ui/hooks/useDebouncedValue';

import { AttributeSchemaList_AttributeSchemas3 } from './graphql-types/AttributeSchemaList_AttributeSchemas3';

import MultiSelectDropdown from '../MultiSelectDropdown';

interface Option {
  label: string;
  value: string;
  id: string;
}

interface Attribute {
  _id: string;
  name: string;
  __typename?: string;
  options?: Option[];
  isDefault?: boolean;
}
const TAGS = gql`
  query AttributeSchemaList_AttributeSchemas3($input: AttributeSchemasInput!) {
    attributeSchemas(input: $input) {
      results {
        _id
        isDefault
        ...AttributeSchema_AttributeSchema
        __typename
      }

      __typename
    }
  }

  fragment AttributeSchema_AttributeSchema on AttributeSchema {
    _id
    name

    isSearchable

    ... on SingleSelectAttributeSchema {
      options {
        id
        label
        __typename
      }
      __typename
    }
    ... on MultiSelectAttributeSchema {
      options {
        id
        label
        __typename
      }
      __typename
    }
    __typename
  }
`;

export const SearchableOrganizationAttributesSelect = React.forwardRef<
  HTMLButtonElement,
  {
    value: {
      _id: string;
      name: string;
      __typename: string;
      options?: Option[];
      [key: string]: any;
    }[];
    onChange: any;
    onBlur: () => void;
  } & PopoverProps
>(({ onChange, value, onBlur, ...popoverProps }, ref) => {
  const firstFieldRef = React.useRef(null);
  const [searchText, setSearchText] = React.useState('');

  const query = useQuery<AttributeSchemaList_AttributeSchemas3>(TAGS, {
    variables: {
      input: {
        limit: 1000000,
        offset: 0,
        filter: {
          ownerKind: 'ORGANIZATION',
        },
      },
    },
    notifyOnNetworkStatusChange: true,
  });

  const filteredTags = React.useMemo(() => {
    const allTags = query.data?.attributeSchemas?.results || [];
    if (!searchText.trim()) return allTags;
    return allTags.filter((tag) =>
      tag.name.toLowerCase().includes(searchText.toLowerCase())
    );
  }, [query.data?.attributeSchemas?.results, searchText]);

  const tagIds = value?.map((tag) => tag._id);

  const updateSelection = (selectedTag: {
    _id: string;
    name: string;
    isDefault?: boolean;
    __typename?: string;
  }) => {
    const updatedValue = tagIds?.includes(selectedTag._id)
      ? value?.filter((tag) => tag._id !== selectedTag._id)
      : value
      ? [...value, selectedTag]
      : [selectedTag];

    const formattedValue = updatedValue.reduce<{
      defaultAttributes: Attribute[];
      customAttributes: Attribute[];
    }>(
      (acc, curr) => {
        if (curr.isDefault) {
          acc['defaultAttributes'].push({
            ...curr,
            _id: curr._id || curr.name,
          });
        } else {
          acc['customAttributes'].push({ ...curr, _id: curr._id || curr.name });
        }
        return acc;
      },
      { defaultAttributes: [], customAttributes: [] }
    );

    return formattedValue;
  };
  const handleMultiSelectChange = (selectedValues: string[], tagId: string) => {
    const updatedValue = value.map((tag) =>
      tag._id === tagId
        ? { ...tag, schemaId: tagId, values: selectedValues }
        : tag
    );

    const formattedValue = updatedValue.reduce<{
      defaultAttributes: Attribute[];
      customAttributes: Attribute[];
    }>(
      (acc, curr) => {
        if (curr.isDefault) {
          acc['defaultAttributes'].push({
            ...curr,
            _id: curr._id || curr.name,
          });
        } else {
          acc['customAttributes'].push({ ...curr, _id: curr._id || curr.name });
        }
        return acc;
      },
      { defaultAttributes: [], customAttributes: [] }
    );
    onChange(formattedValue);
    onBlur();
  };

  const handleFreeTextChange = (text: string[], tagId: string) => {
    const updatedValue = value.map((tag) =>
      tag._id === tagId ? { ...tag, values: text, schemaId: tagId } : tag
    );

    const formattedValue = updatedValue.reduce<{
      defaultAttributes: Attribute[];
      customAttributes: Attribute[];
    }>(
      (acc, curr) => {
        if (curr.isDefault) {
          acc['defaultAttributes'].push({
            ...curr,
            _id: curr._id || curr.name,
          });
        } else {
          acc['customAttributes'].push({ ...curr, _id: curr._id || curr.name });
        }
        return acc;
      },
      { defaultAttributes: [], customAttributes: [] }
    );
    onChange(formattedValue);
    onBlur();
  };

  const renderAttributeInput = (tag: (typeof value)[0]) => {
    switch (tag.__typename) {
      case 'MultiSelectAttributeSchema':
        return (
          tag.options && (
            <MultiSelectDropdown
              options={tag.options}
              value={tag.values || []}
              onChange={(selectedValues) =>
                handleMultiSelectChange(selectedValues, tag._id)
              }
              placeholder={`Enter ${tag.name}`}
            />
          )
        );
      case 'SingleSelectAttributeSchema':
        return (
          tag.options && (
            <MultiSelectDropdown
              options={tag.options}
              value={tag.values || []}
              onChange={(selectedValues) =>
                handleMultiSelectChange(selectedValues, tag._id)
              }
              placeholder={`Select ${tag.name}`}
            />
          )
        );
      case 'FreeTextAttributeSchema':
        return (
          <Input
            w="full"
            value={tag.values || ''}
            onChange={(e) => handleFreeTextChange([e.target.value], tag._id)}
            placeholder={`Enter ${tag.name}`}
          />
        );
      default:
        return (
          <Input
            w="full"
            value={tag.values || ''}
            onChange={(e) => handleFreeTextChange([e.target.value], tag._id)}
            placeholder={`Enter ${tag.name}`}
          />
        );
    }
  };
  return (
    <Popover initialFocusRef={firstFieldRef} matchWidth {...popoverProps}>
      <PopoverTrigger>
        <SelectButton ref={ref} w="full" onBlur={onBlur}>
          {value && value.length > 0 ? (
            <HStack>
              {value.map((tag) => {
                return (
                  <Tag key={tag._id} variant="solid">
                    <TagLabel>{tag.name}</TagLabel>
                  </Tag>
                );
              })}
            </HStack>
          ) : (
            'Select Organization Attribute'
          )}
        </SelectButton>
      </PopoverTrigger>
      <Portal>
        <PopoverContent width="full" shadow="lg" maxHeight={56} overflow="auto">
          <FocusLock returnFocus persistentFocus={false}>
            <PopoverMenuList>
              <InputGroup>
                <Input
                  ref={firstFieldRef}
                  value={searchText}
                  onChange={(event) => setSearchText(event.target.value)}
                  placeholder="Search Organization Attribute"
                  autoComplete="off"
                />
                {query.loading ? (
                  <InputRightElement>
                    <Spinner color="gray.600" size="sm" />
                  </InputRightElement>
                ) : !!searchText ? (
                  <InputRightElement>
                    <IconButton
                      aria-label="clear search"
                      icon={<XIcon />}
                      onClick={() => setSearchText('')}
                      size="sm"
                      variant="ghost"
                      colorScheme="gray"
                    />
                  </InputRightElement>
                ) : null}
              </InputGroup>
              {filteredTags?.map(
                (tag: { _id: string; name: string; isDefault?: boolean }) => {
                  return (
                    <PopoverMenuItem
                      key={tag._id}
                      onClick={() => onChange(updateSelection(tag))}
                      isChecked={value && tagIds?.includes(tag._id)}
                    >
                      {tag.name}
                    </PopoverMenuItem>
                  );
                }
              )}

              {filteredTags?.length === 0 && (
                <Text
                  color="gray.500"
                  padding="2"
                  textStyle="compact"
                  textAlign="center"
                >
                  No matching search results
                </Text>
              )}
            </PopoverMenuList>
          </FocusLock>
        </PopoverContent>
      </Portal>
      {/* Render additional fields based on __typename */}
      {value.map((tag) => (
        <Box key={tag._id} my={2}>
          <FormControl
            isInvalid={
              !!(tag.__typename && (!tag.values || !tag.values.length))
            }
          >
            <FormLabel>{tag.name}</FormLabel>

            {renderAttributeInput(tag)}

            <FormErrorMessage>
              Please enter a value for {tag.name}
            </FormErrorMessage>
          </FormControl>
        </Box>
      ))}{' '}
    </Popover>
  );
});

SearchableOrganizationAttributesSelect.displayName =
  'SearchableOrganizationAttributesSelect';
